import { useMemo } from 'react';

import { useMultiFlightMap } from '../../MultiFlightMap.context';
import {
  getMemberIds,
  hasUngroupedItems,
  selectGroupsInUse,
  toFilterGroupViews,
  toGroupViews
} from '../domain/groupQueries';
import { validateGroupName } from '../domain/groupRules';
import { createFamilyBridge } from './groupingBridge';

import type { GroupChange, GroupFamily, GroupLike, GroupNameError, GroupView } from '../domain/grouping.types';
import type { GroupChangeSummary } from './groupingBridge';

export interface UseGroupingResult {
  /** Visible groups selected items can be added to ("Add to existing group"): cartographic ones are excluded. */
  addableGroups: GroupView[];
  /** Groups of the list filters: {@link groups} plus, for screens, the default "UnGrouped" group (first, flagged). */
  filterGroups: GroupView[];
  /** Every group of the family (including hidden and deleted ones), for the rule functions. */
  allGroups: GroupLike[];
  /** The ONE write path: resolves the change and writes items, groups, ads and temporal pacing. */
  applyGroupChange: (change: GroupChange) => GroupChangeSummary;
  /** Visible groups the user can edit / add items to. */
  editableGroups: GroupView[];
  /** UI ids of the non-deleted items of a group. */
  getMemberIds: (groupId: string) => string[];
  /** Whether some non-deleted item belongs to no visible group (drives the virtual "Ungrouped" filter option). */
  hasUngroupedItems: boolean;
  /** Visible groups in use in the current map (tags, menus, filters) with member counts. */
  groups: GroupView[];
  /**
   * Groups in use in the current map (see {@link selectGroupsInUse}): scope of every UI projection and of the name
   * uniqueness checks. Venues hide the user's group history from other campaigns; other families keep every group.
   */
  inUseGroups: GroupLike[];
  /** Resolves a change without writing anything (e.g. to decide whether to show the delete prompt). */
  previewGroupChange: (change: GroupChange) => GroupChangeSummary;
  validateName: (name: string, excludeGroupId?: string) => GroupNameError | null;
}

/** Grouping state and operations of one family, bridged to the MultiFlightMap context. */
export function useGrouping(family: GroupFamily): UseGroupingResult {
  const context = useMultiFlightMap();
  const bridge = createFamilyBridge(family, context);
  const { groups: allGroups, items } = bridge;
  const existedOnLoad = context.groupsExistingOnLoad[family];

  // Only the UI projections are narrowed: the bridge keeps writing the full list, so hidden groups are preserved
  const inUseGroups = useMemo(
    () => selectGroupsInUse(family, allGroups, items, existedOnLoad),
    [family, allGroups, items, existedOnLoad]
  );
  const groups = useMemo(() => toGroupViews(family, inUseGroups, items), [family, inUseGroups, items]);
  const filterGroups = useMemo(() => toFilterGroupViews(family, inUseGroups, items), [family, inUseGroups, items]);
  const ungroupedItemsExist = useMemo(() => hasUngroupedItems(groups, items), [groups, items]);
  const editableGroups = useMemo(() => groups.filter(group => group.editable), [groups]);
  const addableGroups = useMemo(() => groups.filter(group => group.capabilities.canEditMembers), [groups]);

  return {
    addableGroups,
    allGroups,
    applyGroupChange: change => bridge.run(change, true),
    editableGroups,
    filterGroups,
    getMemberIds: groupId => getMemberIds(family, items, groupId),
    groups,
    hasUngroupedItems: ungroupedItemsExist,
    inUseGroups,
    previewGroupChange: change => bridge.run(change, false),
    validateName: (name, excludeGroupId) => validateGroupName(name, inUseGroups, excludeGroupId)
  };
}
